/**
 * Browse past Ace stability test runs for the current review session.
 */

import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  agentReviewAPI,
  aceStabilityTestTimeoutMs,
  type AceStabilityTestHistoryItem,
} from "@/services/api/agentReview";
import { AceStabilityResultView } from "./AceStabilityResultView";
import type { BackgroundAceStabilityState } from "./useAceStabilityTest";

export interface AceStabilityHistoryDialogProps {
  open: boolean;
  catalogueKey: string;
  sessionId: string | null;
  onClose: () => void;
  onTrackRun: (state: BackgroundAceStabilityState) => void;
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function caseLabel(item: AceStabilityTestHistoryItem): string {
  return item.subject_label?.trim() || item.run_id?.slice(-8) || "Case";
}

function outcomeLabel(item: AceStabilityTestHistoryItem): string {
  if (item.status === "failed") return "Failed";
  if (item.status === "cancelled") return "Cancelled";
  if (item.status === "queued" || item.status === "processing") return "In progress";
  if (item.stable === true) return "Stable";
  if (item.stable === false) return "Variance";
  return "Completed";
}

function isTerminal(item: AceStabilityTestHistoryItem): boolean {
  return ["completed", "failed", "cancelled"].includes(item.status);
}

export function AceStabilityHistoryDialog({
  open,
  catalogueKey,
  sessionId,
  onClose,
  onTrackRun,
}: AceStabilityHistoryDialogProps) {
  const [items, setItems] = useState<AceStabilityTestHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!catalogueKey) return;
    setLoading(true);
    setError(null);
    try {
      const response = await agentReviewAPI.listAceStabilityTestHistory(catalogueKey, sessionId);
      setItems(response.items);
      setSelectedId((current) => {
        if (current && response.items.some((item) => item.task_id === current)) {
          return current;
        }
        const firstWithResult = response.items.find((item) => item.result);
        return firstWithResult?.task_id ?? response.items[0]?.task_id ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load stability history");
      setItems([]);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, [catalogueKey, sessionId]);

  useEffect(() => {
    if (!open) return;
    void loadHistory();
  }, [open, loadHistory]);

  const selected = items.find((item) => item.task_id === selectedId) ?? null;

  const handleTrack = (item: AceStabilityTestHistoryItem) => {
    const label = caseLabel(item);
    onTrackRun({
      taskId: item.task_id,
      caseLabel: label,
      message: item.status,
      progress: null,
      startedAt: item.created_at ? new Date(item.created_at).getTime() : Date.now(),
      executionStartedAt: null,
      iterations: item.iterations_requested ?? 10,
      maxWaitMs: aceStabilityTestTimeoutMs(item.iterations_requested ?? 10, catalogueKey),
      result: item.result ?? null,
      error: item.error ?? null,
      trackingPaused: false,
      queuedBehindAnother: item.status === "queued",
      blockingTaskLabel: null,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="5xl"
      className="w-[min(96vw,72rem)]"
      data-testid="ace-stability-history-dialog"
    >
      <DialogTitle>Ace stability history</DialogTitle>
      <DialogBody className="flex min-h-[70vh] flex-col space-y-4">
        <p className="text-sm text-slate-400 leading-relaxed">
          Past stability tests for this task type and session. Select a run to view variance
          details, or track an in-progress job on the Agent Review screen.
        </p>

        {loading ? <LoadingPlaceholder text="Loading history…" /> : null}
        {error ? <p className="text-sm text-rose-400">{error}</p> : null}

        {!loading && !error && items.length === 0 ? (
          <p className="text-sm text-slate-500">No stability tests yet for this session.</p>
        ) : null}

        {!loading && items.length > 0 ? (
          <div className="grid min-h-0 flex-1 gap-5 lg:grid-cols-[minmax(18rem,22rem)_minmax(0,1fr)]">
            <div className="min-h-[16rem] overflow-y-auto rounded-lg ring-1 ring-white/10 lg:min-h-0">
              <table className="min-w-full text-left text-sm">
                <thead className="sticky top-0 z-10 bg-slate-900 text-slate-500">
                  <tr>
                    <th className="px-3 py-2.5 font-medium">When</th>
                    <th className="px-3 py-2.5 font-medium">Case</th>
                    <th className="px-3 py-2.5 font-medium">Profile</th>
                    <th className="px-3 py-2.5 font-medium">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.task_id}
                      className={clsx(
                        "border-t border-white/5 cursor-pointer hover:bg-white/[0.03]",
                        selectedId === item.task_id && "bg-indigo-500/[0.08]",
                      )}
                      onClick={() => setSelectedId(item.task_id)}
                    >
                      <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">
                        {formatWhen(item.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-slate-200">
                        {caseLabel(item)}
                      </td>
                      <td className="px-3 py-2.5 text-slate-400 capitalize">
                        {item.ace_profile ?? "—"}
                      </td>
                      <td className="px-3 py-2.5">
                        <span
                          className={clsx(
                            "rounded px-1.5 py-0.5 text-[11px] font-medium",
                            item.stable === true && "bg-emerald-500/15 text-emerald-300",
                            item.stable === false && "bg-amber-500/15 text-amber-300",
                            item.status === "failed" && "bg-rose-500/15 text-rose-300",
                            !isTerminal(item) && "bg-indigo-500/15 text-indigo-300",
                            item.stable == null &&
                              isTerminal(item) &&
                              item.status !== "failed" &&
                              "bg-slate-500/15 text-slate-300",
                          )}
                        >
                          {outcomeLabel(item)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="min-h-[16rem] overflow-y-auto rounded-lg ring-1 ring-white/10 p-4 lg:min-h-0">
              {selected?.result ? (
                <AceStabilityResultView result={selected.result} catalogueKey={catalogueKey} />
              ) : selected ? (
                <div className="space-y-2 text-sm text-slate-400">
                  <p>
                    <span className="text-slate-200">{caseLabel(selected)}</span>
                    {" · "}
                    {selected.ace_profile ?? "profile unknown"}
                    {selected.iterations_requested
                      ? ` · ${selected.iterations_requested} iterations`
                      : null}
                  </p>
                  <p>Status: {selected.status}</p>
                  {selected.error ? (
                    <p className="text-rose-300">{selected.error}</p>
                  ) : (
                    <p>Results will appear here when the run completes.</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Select a run to view details.</p>
              )}
            </div>
          </div>
        ) : null}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={() => void loadHistory()} disabled={loading}>
          Refresh
        </Button>
        <Button plain onClick={onClose}>
          Close
        </Button>
        {selected && !isTerminal(selected) ? (
          <Button onClick={() => handleTrack(selected)} data-testid="ace-stability-history-track">
            Track on dashboard
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
